import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Button, Grid, Paper, Alert, CircularProgress } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { SignupData, Person, OfficerType } from 'form-application-shared';
import { formApi } from '../services/api'; 

interface SupportingDocumentsStepProps {
  formData: SignupData;
  setFormData: React.Dispatch<React.SetStateAction<SignupData>>;
  onValidationChange: (validationFn: () => boolean) => void;
  formId: string | null;
}

interface RequiredUpload {
  id: string;
  name: string;
  types: string[]; // Multiple types if person has multiple roles
}

interface DocumentErrors {
  [key: string]: string;
}

const SupportingDocumentsStep: React.FC<SupportingDocumentsStepProps> = ({ 
  formData, 
  setFormData, 
  onValidationChange,
  formId
}) => {
  const [errors, setErrors] = useState<DocumentErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});

  // Helper function to get officer type label
  const getOfficerTypeLabel = (officerType: OfficerType): string => {
    switch (officerType) {
      case OfficerType.PRESIDENT:
        return 'President';
      case OfficerType.SECRETARY:
        return 'Secretary';
      case OfficerType.TREASURER:
        return 'Treasurer';
      case OfficerType.VICE_PRESIDENT:
        return 'Vice President';
      default:
        return '';
    }
  };

  // Get all names that need ID uploads
  const getRequiredUploads = useCallback((): RequiredUpload[] => {
    // Skip uploads if reinsurance is not enabled
    if (!formData.hasReinsurance) {
      return [];
    }
    
    // Use a map to track unique people and their roles
    const uploadMap = new Map<string, RequiredUpload>();
    
    // Process all people
    formData.people.forEach((person, index) => {
      // Skip empty entries
      if (!person.legalName) {
        return;
      }
      
      const types: string[] = [];
      
      // Add officer types if applicable
      person.officerTypes.forEach(officerType => {
        if (officerType !== OfficerType.NONE) {
          types.push(getOfficerTypeLabel(officerType));
        }
      });
      
      // Add shareholder type if applicable
      if (person.isShareholder) {
        types.push('Shareholder');
      }
      
      // Add director type if applicable
      if (person.isDirector) {
        types.push('Director');
      }
      
      // If the person has roles, add them to the map
      if (types.length > 0) {
        if (uploadMap.has(person.legalName)) {
          // Person already exists, merge the types
          const existing = uploadMap.get(person.legalName)!;
          const mergedTypes = [...new Set([...existing.types, ...types])];
          uploadMap.set(person.legalName, {
            ...existing,
            types: mergedTypes
          });
        } else {
          // New person
          uploadMap.set(person.legalName, {
            id: `person_${index}`,
            name: person.legalName,
            types: types
          });
        }
      }
    });
    
    // Convert map to array
    return Array.from(uploadMap.values());
  }, [formData.hasReinsurance, formData.people]);

  // Improved validation function that checks for valid fileId
  const validateStep = useCallback(() => {
    // If reinsurance is not enabled, no validation needed
    if (!formData.hasReinsurance) {
      return true;
    }
    
    let isValid = true;
    const newErrors: DocumentErrors = {};
    
    // Get all required uploads
    const requiredUploads = getRequiredUploads();
    
    // Check if each required upload has a file with a valid fileId
    requiredUploads.forEach(upload => {
      const uploadData = formData.idUploads[upload.name];
      
      if (!uploadData || !uploadData.fileId) {
        newErrors[upload.name] = `Valid ID upload required for ${upload.name}`;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  }, [formData.hasReinsurance, formData.idUploads, getRequiredUploads]);

  // Add function to mark all fields as touched - wrap in useCallback
  const markAllFieldsAsTouched = useCallback(() => {
    const allFields: Record<string, boolean> = {};
    const requiredUploads = getRequiredUploads();
    
    requiredUploads.forEach(upload => {
      allFields[upload.name] = true;
    });
    
    setTouched(allFields);
  }, [getRequiredUploads]);

  // Create validation wrapper that marks all fields as touched when validating
  const validateStepAndMarkTouched = useCallback(() => {
    markAllFieldsAsTouched();
    return validateStep();
  }, [validateStep, markAllFieldsAsTouched]);

  // Register validation function with parent component
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validateStepAndMarkTouched);
    }
  }, [onValidationChange, validateStepAndMarkTouched]);

  // Clean up old uploads from removed people
  useEffect(() => {
    if (formData.idUploads) {
      const requiredNames = new Set<string>();
      const requiredUploads = getRequiredUploads();
      
      // Create a set of all required names
      requiredUploads.forEach(upload => {
        requiredNames.add(upload.name);
      });
      
      // Remove any uploads that are no longer needed
      const updatedUploads = { ...formData.idUploads };
      let hasChanges = false;
      
      Object.keys(updatedUploads).forEach(name => {
        if (!requiredNames.has(name)) {
          delete updatedUploads[name];
          hasChanges = true;
        }
      });
      
      // Update form data if changes were made
      if (hasChanges) {
        setFormData(prev => ({
          ...prev,
          idUploads: updatedUploads
        }));
      }
    }
  }, [formData.people, formData.idUploads, getRequiredUploads, setFormData]);

  // Validate on each change
  useEffect(() => {
    validateStep();
  }, [validateStep]);
  
  // Force validation when formData.idUploads changes
  useEffect(() => {
    if (formData.idUploads) {
      validateStep();
    }
  }, [formData.idUploads, validateStep]);

  const handleFileUpload = async (personName: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !formId) return;
    
    try {
      // Mark field as touched
      setTouched(prev => ({
        ...prev,
        [personName]: true
      }));
      
      // Set uploading state
      setUploading(prev => ({
        ...prev,
        [personName]: true
      }));
      
      // Create a temporary upload entry with empty fileId
      setFormData(prev => {
        const newFormData = { ...prev };
        if (!newFormData.idUploads) {
          newFormData.idUploads = {};
        }
        
        newFormData.idUploads[personName] = {
          fileId: "", // Empty fileId until upload completes
          name: file.name,
          type: file.type,
          uploadDate: new Date()
        };
        
        return newFormData;
      });
      
      // Upload the file to the server
      const response = await formApi.uploadFile(formId, personName, file);
      
      // Update the form data with the fileId from the server
      setFormData(prev => {
        const newFormData = { ...prev };
        if (!newFormData.idUploads) {
          newFormData.idUploads = {};
        }
        
        newFormData.idUploads[personName] = {
          fileId: response.fileId,
          name: file.name,
          type: file.type,
          uploadDate: new Date()
        };
        
        return newFormData;
      });
      
      // Clear any errors for this field immediately after successful upload
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[personName];
        return newErrors;
      });
      
    } catch (error) {
      // Handle upload error
      console.error('File upload failed:', error);
      
      // Remove the temporary upload entry since it failed
      setFormData(prev => {
        const newFormData = { ...prev };
        if (newFormData.idUploads && newFormData.idUploads[personName]) {
          // Only delete if fileId is empty (temporary entry)
          if (!newFormData.idUploads[personName].fileId) {
            delete newFormData.idUploads[personName];
          }
        }
        return newFormData;
      });
      
      setErrors(prev => ({
        ...prev,
        [personName]: 'Upload failed. Please try again.'
      }));
    } finally {
      // Clear uploading state
      setUploading(prev => ({
        ...prev,
        [personName]: false
      }));
    }
  };

  // Function to get upload status text and color
  const getUploadStatus = (personName: string) => {
    const upload = formData.idUploads?.[personName];
    
    if (!upload) {
      return {
        text: "No file uploaded",
        color: "textSecondary"
      };
    }
    
    if (!upload.fileId) {
      return {
        text: "Upload pending...",
        color: "warning.main"
      };
    }
    
    return {
      text: `${upload.name} ✓`,
      color: "success.main"
    };
  };

  // If reinsurance is not enabled, show a message and skip this step
  if (!formData.hasReinsurance) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Supporting Documents
        </Typography>
        <Alert severity="info">
          Supporting documents are not required because reinsurance is not enabled.
        </Alert>
      </Box>
    );
  }

  const requiredUploads = getRequiredUploads();
  
  return (
    <>
      <Typography variant="h5" gutterBottom>
        Supporting Documents
      </Typography>
      
      <Box mt={2} mb={3}>
        <Typography variant="body1">
          Please upload a copy of government-issued ID for each person listed below.
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Accepted formats up to 20MB: .pdf, .png, .jpg, .jpeg, .tiff, .heic, .heif
        </Typography>
      </Box>
      
      {requiredUploads.length === 0 ? (
        <Alert severity="info">
          No uploads are required. Please make sure you have entered information for officers, directors, and shareholders in the previous steps.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {requiredUploads.map((upload) => {
            const status = getUploadStatus(upload.name);
            const hasError = touched[upload.name] && errors[upload.name];
            
            return (
              <Grid item xs={12} key={upload.id}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle1">
                        {upload.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {upload.types.join(' / ')}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif,.heic,.heif"
                          style={{ display: 'none' }}
                          id={`id-upload-${upload.id}`}
                          type="file"
                          onChange={(e) => handleFileUpload(upload.name, e)}
                          disabled={uploading[upload.name]}
                        />
                        <label htmlFor={`id-upload-${upload.id}`}>
                          <Button
                            variant="contained"
                            component="span"
                            startIcon={uploading[upload.name] ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                            color={hasError ? "error" : "primary"}
                            disabled={uploading[upload.name]}
                          >
                            {uploading[upload.name] ? 'Uploading...' : 
                              formData.idUploads?.[upload.name]?.fileId ? 'Replace ID' : 'Upload ID'}
                          </Button>
                        </label>
                        
                        <Box sx={{ ml: 2 }}>
                          {hasError ? (
                            <Typography color="error" variant="caption">
                              {errors[upload.name]}
                            </Typography>
                          ) : (
                            <>
                              <Typography variant="body2" color={status.color}>
                                {status.text}
                              </Typography>
                              {formData.idUploads?.[upload.name]?.uploadDate && (
                                <Typography variant="caption" color="textSecondary">
                                  Uploaded {new Date(formData.idUploads[upload.name].uploadDate).toLocaleString()}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </>
  );
};

export default SupportingDocumentsStep;